import { Link } from "../navigation";
import { ArrowRight, Eye, Plus, Search } from "lucide-react";
import type { LibraryExam } from "../../../../packages/published-content/library";
import { displayTitle } from "../portal-data";
import { testCatalogStatus, testCatalogStatusLabel, testSectionAvailability } from "./test-catalog-status";

function parseExamSitting(sitting?: string | null) {
  if (sitting && /^\d{4}-\d{2}/.test(sitting)) {
    const [year, month] = sitting.split("-");
    const date = new Date(Date.UTC(Number(year), Number(month) - 1, 1, 12));
    return {
      year,
      month: date.toLocaleDateString("en", { month: "short", timeZone: "UTC" })
    };
  }
  return { year: "SAT", month: "Form" };
}

export function SearchTests({
  search,
  onSearch,
  placeholder = "Search tests"
}: {
  search: string;
  onSearch: (value: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="test-search">
      <Search size={16} />
      <input
        aria-label={placeholder}
        type="search"
        placeholder={placeholder}
        value={search}
        onChange={(event) => onSearch(event.target.value)}
      />
    </label>
  );
}

type TestStatus = { label: string; tone: "good" | "alert" | "accent" | "neutral" };

export function TestRow({ exam, admin, onAction, scope = "full", actionLabel, status, detail, disabled = false }: {
  exam: LibraryExam;
  admin: boolean;
  onAction: () => void;
  scope?: "full" | "rw" | "math";
  actionLabel?: string;
  status?: TestStatus;
  detail?: string;
  disabled?: boolean;
}) {
  const { year, month } = parseExamSitting(exam.sitting);
  const catalogStatus = testCatalogStatus(exam);
  const isFull = catalogStatus === "full" && scope === "full";
  const sectionAvailability = scope === "full" ? null : testSectionAvailability(exam, scope);
  const format = scope === "rw" ? "Reading and Writing" : scope === "math" ? "Math" : isFull ? "Full exam" : "Partial exam";
  const timing = scope === "full" && exam.supportsTiming ? "Timed or untimed" : scope === "full" ? "Untimed" : `${scope === "math" ? 70 : 64} minutes timed`;

  return (
    <article className="test-row">
      <div className="test-card-top">
        <span className="test-date">{month} {year}</span>
        {status ? <span className={`status-badge ${status.tone}`}>{status.label}</span> : admin ? <span className={`status-badge ${catalogStatus === "full" ? "good" : "alert"}`}>{testCatalogStatusLabel(exam)}</span> : null}
      </div>
      <div className="test-info">
        <h2>
          {admin ? (
            <Link href={`/admin/tests/${encodeURIComponent(exam.id)}`}>{displayTitle(exam.title)}</Link>
          ) : (
            displayTitle(exam.title)
          )}
        </h2>
        <p className="test-card-meta"><span>{format}</span><span>{scope === "full" ? (catalogStatus === "partial" ? `${exam.available} of ${exam.expected} questions available` : `${exam.available} questions`) : sectionAvailability ? `${sectionAvailability.available} of ${sectionAvailability.expected} questions available` : "Available questions"}</span></p>
        <p className="test-card-detail">{detail ?? timing}</p>
        {admin && <p className="test-card-detail">{exam.difficulty?.medianBand != null ? `Estimated difficulty ${exam.difficulty.medianBand}/7 · ${exam.difficulty.classified}/${exam.difficulty.represented} questions rated${exam.difficulty.classified < exam.difficulty.represented ? " (partial rating)" : ""}` : "Difficulty unrated"}</p>}
      </div>
      <div className="test-actions">
        {admin && (
          <Link className="secondary-button" href={`/admin/tests/${encodeURIComponent(exam.id)}`}>
            <Eye size={14} /> View
          </Link>
        )}
        <button type="button" className="primary-button" disabled={disabled} onClick={onAction}>
          {admin ? <Plus size={14} /> : null}
          {admin ? "Assign" : actionLabel ?? "Start"}
          {!admin && <ArrowRight size={14} />}
        </button>
      </div>
    </article>
  );
}
